import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { v4 as uuidv4 } from 'uuid';
import { IDeliverableConfig, IDeliverableImageForm, TDeliverable } from '../deliverable-list.interface';
import { ProductPlacementComponent } from './product-placement.component';
import { IProductPlacementDeliverableForm } from './product-placement.interface';

@Injectable({
  providedIn: 'root',
})
export class ProductPlacementService {
  private readonly translate = inject(TranslateService);

  public getConfig(): IDeliverableConfig {
    return {
      component: ProductPlacementComponent,
      createForm: (
        item?: TDeliverable,
        creatorId?: string,
        isViewMode?: boolean,
      ): FormGroup<IProductPlacementDeliverableForm> | null => {
        if (item && item.type !== EDeliverableType.productPlacement) {
          return null;
        }

        const form = new FormGroup({
          customCtaValues: new FormControl<ICustomCtaValue[]>(item?.customCtaValues ?? []),
          exclusiveTo: new FormControl<string | null>(item?.exclusiveTo || creatorId || null),
          globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(item?.globalCtaValues ?? null),
          id: new FormControl<string | null>(item?.id ?? uuidv4()),
          images: new FormArray<FormGroup<IDeliverableImageForm>>(
            (item?.images ?? [{ id: uuidv4(), imageFile: null, imageId: null }]).map((image) => {
              const mockFile =
                image?.imageId && !image?.imageFile ? FileUploaderService.createCustomFile(image.imageId) : null;

              return new FormGroup<IDeliverableImageForm>({
                customCtaValues: new FormControl<ICustomCtaValue[]>(image.customCtaValues ?? [], {
                  nonNullable: true,
                }),
                exclusiveTo: new FormControl<string | null>(image.exclusiveTo || creatorId || null),
                globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(image.globalCtaValues ?? null),
                id: new FormControl<string | null>(image?.id ?? uuidv4()),
                imageFile: new FormControl<ICustomFile | null>(
                  mockFile ?? image?.imageFile ?? null,
                  isViewMode ? [] : [Validators.required, imageExtensionValidator()],
                ),
                imageId: new FormControl<string | null>(image?.imageId),
              });
            }),
          ),
          isNew: new FormControl<boolean>(!item?.id),
          name: new FormControl<string | null>(
            item?.name ??
              this.translate.instant(
                'pages.campaignForm.ctaDialog.ctaDetails.deliverables.productPlacementsDeliverable.title',
              ),
            [Validators.required, Validators.maxLength(255)],
          ),
          productName: new FormControl<string | null>(
            item?.productName ?? '',
            isViewMode ? [] : [Validators.required, Validators.maxLength(255)],
          ),
          sponsoredContentRelevant: new FormControl<boolean>(item?.sponsoredContentRelevant ?? true, {
            nonNullable: true,
          }),
          type: new FormControl<EDeliverableType.productPlacement>(EDeliverableType.productPlacement, {
            nonNullable: true,
          }),
        }) as FormGroup<IProductPlacementDeliverableForm>;

        if (isViewMode) {
          form.disable();
        }

        return form;
      },
    };
  }
}
